import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api-client';
import { useAuthStore } from './auth-store';
import { logCustomChecklistAction, logChecklistAction } from '@/lib/activity-logger';

export interface CustomChecklist {
  id: string;
  name: string;
  order?: number;
  showOnDashboard?: boolean;
  recurring?: boolean;
  recurringType?: 'daily' | 'weekly';
  recurringTime?: number; // Hour 0-23 for daily
  recurringDay?: number; // Day 0-6 for weekly (0=Sunday)
  lastReset?: string; // Timestamp of last reset
  color?: string; // Hex color for icon and progress bar
  createdAt: string;
  updatedAt: string;
}

export interface CustomChecklistItem {
  id: string;
  listId: string;
  text: string;
  completed: boolean;
  order?: number;
  createdAt: string;
  updatedAt: string;
}

interface CustomChecklistState {
  lists: CustomChecklist[];
  items: CustomChecklistItem[];
  isSyncing: boolean;
  createList: (name: string, showOnDashboard?: boolean, recurring?: boolean, recurringType?: 'daily' | 'weekly', recurringTime?: number, recurringDay?: number, color?: string) => Promise<CustomChecklist>;
  updateList: (id: string, updates: { name?: string; showOnDashboard?: boolean; recurring?: boolean; recurringType?: 'daily' | 'weekly'; recurringTime?: number; recurringDay?: number; color?: string }) => void;
  deleteList: (id: string) => void;
  moveListUp: (id: string) => void;
  moveListDown: (id: string) => void;
  createItem: (listId: string, text: string) => CustomChecklistItem | null;
  moveItemUp: (id: string) => void;
  moveItemDown: (id: string) => void;
  updateItem: (id: string, text: string) => void;
  deleteItem: (id: string) => void;
  toggleItem: (id: string) => void;
  clearCompletedForList: (listId: string) => Promise<void>;
  uncheckAllForList: (listId: string) => Promise<void>;
  checkRecurring: () => Promise<void>;
  getItemsForList: (listId: string) => CustomChecklistItem[];
  fetchFromServer: () => Promise<void>;
  clearAndSync: () => Promise<void>;
}

const genId = (prefix = '') => `${prefix}${Date.now().toString(36)}-${Math.random().toString(36).slice(2,8)}`;

export const useCustomChecklistStore = create<CustomChecklistState>()(
  persist(
    (set, get) => ({
      lists: [],
      items: [],
      isSyncing: false,

      createList: async (name: string, showOnDashboard?: boolean, recurring?: boolean, recurringType?: 'daily' | 'weekly', recurringTime?: number, recurringDay?: number, color?: string) => {
        const now = new Date().toISOString();
        // Determine next order for lists (append to end)
        const existing = get().lists;
        const maxOrder = existing.length ? Math.max(...existing.map(l => (typeof l.order === 'number' ? l.order : 0))) : 0;
        const list: CustomChecklist = { 
          id: genId('cl_'), 
          name: name.trim() || 'Ny lista', 
          order: maxOrder + 1,
          showOnDashboard,
          recurring,
          recurringType,
          recurringTime,
          recurringDay,
          lastReset: recurring ? now : undefined,
          color: color || '#ff6b35',
          createdAt: now, 
          updatedAt: now 
        };
        // Optimistic local update
        set((state) => ({ lists: [...state.lists, list] }));

        // Try server create if authenticated
        try {
          const { isAuthenticated } = useAuthStore.getState();
          if (!isAuthenticated) return list;
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId = getEffectiveFamilyOwnerId();
          const familyId = ownerId || useAuthStore.getState().user?.familyId;
          const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
          const res = await apiPost<{ list: CustomChecklist }>(`/custom-checklists/create${qs}`, { 
            name: list.name, 
            showOnDashboard,
            recurring,
            recurringType,
            recurringTime,
            recurringDay,
            color,
          });
          if (res.success && res.data?.list) {
            logCustomChecklistAction('CREATE_CUSTOM_CHECKLIST', res.data.list.id, { name: list.name });
            // Replace temp id with server id
            set((state) => ({ lists: state.lists.map(l => l.id === list.id ? res.data!.list : l) }));
            return res.data.list;
          }
        } catch {
          // Keep the optimistic local list if the server create fails.
        }

        return list;
      },

      updateList: (id: string, updates: { name?: string; showOnDashboard?: boolean; recurring?: boolean; recurringType?: 'daily' | 'weekly'; recurringTime?: number; recurringDay?: number; color?: string }) => {
        const now = new Date().toISOString();
        
        // Initialize lastReset when enabling recurring for the first time
        const currentList = get().lists.find(l => l.id === id);
        const shouldInitializeLastReset = !currentList?.recurring && updates.recurring === true;
        
        set((state) => ({ 
          lists: state.lists.map(l => 
            l.id === id 
              ? { 
                  ...l, 
                  ...updates,
                  name: updates.name !== undefined ? updates.name.trim() : l.name,
                  color: updates.color !== undefined ? updates.color : (l.color || '#ff6b35'),
                  lastReset: shouldInitializeLastReset ? now : l.lastReset,
                  updatedAt: now 
                } 
              : l
          ) 
        }));
        
        (async () => {
          const { isAuthenticated } = useAuthStore.getState();
          if (!isAuthenticated) return;
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId2 = getEffectiveFamilyOwnerId();
          const familyId2 = ownerId2 || useAuthStore.getState().user?.familyId;
          const qs2 = familyId2 ? `?familyId=${encodeURIComponent(familyId2)}` : '';
          await apiPut(`/custom-checklists/update/${id}${qs2}`, updates);
          logCustomChecklistAction('UPDATE_CUSTOM_CHECKLIST', id, updates);
        })();
      },

      deleteList: (id: string) => {
        // Optimistic
        const prev = { lists: get().lists, items: get().items };
        set((state) => ({ lists: state.lists.filter(l => l.id !== id), items: state.items.filter(it => it.listId !== id) }));
        (async () => {
          const { isAuthenticated } = useAuthStore.getState();
          if (!isAuthenticated) return;
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId3 = getEffectiveFamilyOwnerId();
          const familyId3 = ownerId3 || useAuthStore.getState().user?.familyId;
          const qs3 = familyId3 ? `?familyId=${encodeURIComponent(familyId3)}` : '';
          const res = await apiDelete(`/custom-checklists/delete/${id}${qs3}`);
          if (!res.success) {
            // rollback
            set({ lists: prev.lists, items: prev.items });
          } else {
            logCustomChecklistAction('DELETE_CUSTOM_CHECKLIST', id);
          }
        })();
      },

      moveListUp: (id: string) => {
        const lists = get().lists.slice();
        const idx = lists.findIndex(l => l.id === id);
        if (idx === -1) return;
        const list = lists[idx];
        
        // Sort lists by order
        let sortedLists = lists.slice().sort((a,b) => (typeof a.order === 'number' ? a.order : 0) - (typeof b.order === 'number' ? b.order : 0));
        const pos = sortedLists.findIndex(l => l.id === id);
        if (pos <= 0) return; // already at top

        // Bootstrap sequential orders if needed
        const needsBootstrap = sortedLists.some((l, i) => {
          if (typeof l.order !== 'number') return true;
          if (i === 0) return false;
          const prev = sortedLists[i - 1];
          const prevOrder = typeof prev?.order === 'number' ? prev.order : 0;
          return prevOrder >= (typeof l.order === 'number' ? l.order : 0);
        });
        if (needsBootstrap) {
          const boot = sortedLists.map((l, i) => ({ ...l, order: i + 1 }));
          set((state) => ({ lists: state.lists.map(l => {
            const b = boot.find(x => x.id === l.id);
            return b ? { ...l, order: b.order } : l;
          }) }));
          // sync bootstrapped orders to server
          (async () => {
            const { isAuthenticated, user } = useAuthStore.getState();
            if (!isAuthenticated) return;
            const { getEffectiveFamilyOwnerId } = await import('./family-store');
            const ownerId = getEffectiveFamilyOwnerId();
            const familyId = ownerId || user?.familyId;
            const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
            try {
              for (const b of boot) {
                await apiPut(`/custom-checklists/update/${b.id}${qs}`, { order: b.order });
              }
            } catch {
              get().fetchFromServer();
            }
          })();
          // refresh local sortedLists
          sortedLists = get().lists.slice().sort((a,b) => (typeof a.order === 'number' ? a.order : 0) - (typeof b.order === 'number' ? b.order : 0));
        }

        const prev = sortedLists[sortedLists.findIndex(l => l.id === id) - 1];
        // swap orders
        const aOrder = typeof list.order === 'number' ? list.order : 0;
        const bOrder = typeof prev.order === 'number' ? prev.order : 0;
        set((state) => ({ lists: state.lists.map(l => {
          if (l.id === list.id) return { ...l, order: bOrder };
          if (l.id === prev.id) return { ...l, order: aOrder };
          return l;
        }) }));

        // sync to server
        (async () => {
          const { isAuthenticated, user } = useAuthStore.getState();
          if (!isAuthenticated) return;
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId = getEffectiveFamilyOwnerId();
          const familyId = ownerId || user?.familyId;
          const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
          try {
            await apiPut(`/custom-checklists/update/${list.id}${qs}`, { order: bOrder });
            await apiPut(`/custom-checklists/update/${prev.id}${qs}`, { order: aOrder });
          } catch {
            get().fetchFromServer();
          }
        })();
      },

      moveListDown: (id: string) => {
        const lists = get().lists.slice();
        const idx = lists.findIndex(l => l.id === id);
        if (idx === -1) return;
        const list = lists[idx];
        
        let sortedLists = lists.slice().sort((a,b) => (typeof a.order === 'number' ? a.order : 0) - (typeof b.order === 'number' ? b.order : 0));
        const pos = sortedLists.findIndex(l => l.id === id);
        if (pos === -1 || pos >= sortedLists.length - 1) return; // already at bottom

        // Bootstrap if needed
        const needsBootstrap = sortedLists.some((l, i) => {
          if (typeof l.order !== 'number') return true;
          if (i === 0) return false;
          const prev = sortedLists[i - 1];
          const prevOrder = typeof prev?.order === 'number' ? prev.order : 0;
          return prevOrder >= (typeof l.order === 'number' ? l.order : 0);
        });
        if (needsBootstrap) {
          const boot = sortedLists.map((l, i) => ({ ...l, order: i + 1 }));
          set((state) => ({ lists: state.lists.map(l => {
            const b = boot.find(x => x.id === l.id);
            return b ? { ...l, order: b.order } : l;
          }) }));
          // sync to server
          (async () => {
            const { isAuthenticated, user } = useAuthStore.getState();
            if (!isAuthenticated) return;
            const { getEffectiveFamilyOwnerId } = await import('./family-store');
            const ownerId = getEffectiveFamilyOwnerId();
            const familyId = ownerId || user?.familyId;
            const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
            try {
              for (const b of boot) {
                await apiPut(`/custom-checklists/update/${b.id}${qs}`, { order: b.order });
              }
            } catch {
              get().fetchFromServer();
            }
          })();
          sortedLists = get().lists.slice().sort((a,b) => (typeof a.order === 'number' ? a.order : 0) - (typeof b.order === 'number' ? b.order : 0));
        }

        const next = sortedLists[sortedLists.findIndex(l => l.id === id) + 1];
        // swap orders
        const aOrder = typeof list.order === 'number' ? list.order : 0;
        const bOrder = typeof next.order === 'number' ? next.order : 0;
        set((state) => ({ lists: state.lists.map(l => {
          if (l.id === list.id) return { ...l, order: bOrder };
          if (l.id === next.id) return { ...l, order: aOrder };
          return l;
        }) }));

        // sync to server
        (async () => {
          const { isAuthenticated, user } = useAuthStore.getState();
          if (!isAuthenticated) return;
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId = getEffectiveFamilyOwnerId();
          const familyId = ownerId || user?.familyId;
          const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
          try {
            await apiPut(`/custom-checklists/update/${list.id}${qs}`, { order: bOrder });
            await apiPut(`/custom-checklists/update/${next.id}${qs}`, { order: aOrder });
          } catch {
            get().fetchFromServer();
          }
        })();
      },

      createItem: (listId: string, text: string) => {
        const now = new Date().toISOString();
        if (!text.trim()) return null;
        // Determine next order for this list (append to end)
        const stateNow = get();
        const existing = stateNow.items.filter(i => i.listId === listId);
        const maxOrder = existing.length ? Math.max(...existing.map(i => (typeof i.order === 'number' ? i.order : 0))) : 0;
        const item: CustomChecklistItem = { id: genId('cli_'), listId, text: text.trim(), completed: false, order: maxOrder + 1, createdAt: now, updatedAt: now };
        set((state) => ({ items: [...state.items, item] }));

        (async () => {
          const { isAuthenticated } = useAuthStore.getState();
          if (!isAuthenticated) return;
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId4 = getEffectiveFamilyOwnerId();
          const familyId4 = ownerId4 || useAuthStore.getState().user?.familyId;
          const qs4 = familyId4 ? `?familyId=${encodeURIComponent(familyId4)}` : '';
          const res = await apiPost<{ item: CustomChecklistItem }>(`/custom-checklists/items/create${qs4}`, { listId, text: item.text });
          if (res.success && res.data?.item) {
            set((state) => ({ items: state.items.map(i => i.id === item.id ? res.data!.item : i) }));
          }
        })();

        return item;
      },

      updateItem: (id: string, text: string) => {
        const now = new Date().toISOString();
        set((state) => ({ items: state.items.map(i => i.id === id ? { ...i, text: text.trim(), updatedAt: now } : i) }));
        (async () => {
          const { isAuthenticated } = useAuthStore.getState();
          if (!isAuthenticated) return;
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId5 = getEffectiveFamilyOwnerId();
          const familyId5 = ownerId5 || useAuthStore.getState().user?.familyId;
          const qs5 = familyId5 ? `?familyId=${encodeURIComponent(familyId5)}` : '';
          await apiPut(`/custom-checklists/items/update/${id}${qs5}`, { text });
        })();
      },

      deleteItem: (id: string) => {
        const prev = { items: get().items };
        set((state) => ({ items: state.items.filter(i => i.id !== id) }));
        (async () => {
          const { isAuthenticated } = useAuthStore.getState();
          if (!isAuthenticated) return;
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId6 = getEffectiveFamilyOwnerId();
          const familyId6 = ownerId6 || useAuthStore.getState().user?.familyId;
          const qs6 = familyId6 ? `?familyId=${encodeURIComponent(familyId6)}` : '';
          const res = await apiDelete(`/custom-checklists/items/delete/${id}${qs6}`);
          if (!res.success) set({ items: prev.items });
        })();
      },

      toggleItem: (id: string) => {
        const now = new Date().toISOString();
        const prev = get().items.find(i => i.id === id) || null;
        const item = get().items.find(i => i.id === id);
        const isCompleted = item ? !item.completed : true;
        
        set((state) => ({ items: state.items.map(i => i.id === id ? { ...i, completed: !i.completed, updatedAt: now } : i) }));
        
        // Log activity
        logChecklistAction('TOGGLE_ITEM', id, {
          listId: item?.listId,
          isCompleted,
        });
        
        (async () => {
          const { isAuthenticated, user } = useAuthStore.getState();
          if (!isAuthenticated) return;
          const item = get().items.find(i => i.id === id);
          if (!item) return;
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId = getEffectiveFamilyOwnerId();
          const familyId = ownerId || user?.familyId;
          const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';

          // If the item has a local temporary id (created while offline/unauthenticated),
          // create it on the server first so the subsequent PUT won't return 404.
          let serverItem = item;
          if (item.id.startsWith('cli_')) {
            const createRes = await apiPost<{ item: typeof item }>(`/custom-checklists/items/create${qs}`, { listId: item.listId, text: item.text });
            if (createRes.success && createRes.data?.item) {
              const created = createRes.data.item;
              // Replace temp item with server item
              set((state) => ({ items: state.items.map(i => i.id === item.id ? created : i) }));
              serverItem = created;
            } else {
              // Couldn't create on server — rollback and stop
              if (prev) set((state) => ({ items: state.items.map(i => i.id === id ? prev : i) }));
              return;
            }
          }

          const res = await apiPut(`/custom-checklists/items/update/${serverItem.id}${qs}`, { completed: serverItem.completed });
          if (!res.success && prev) {
            // rollback
            set((state) => ({ items: state.items.map(i => i.id === id ? prev : i) }));
          }
        })();
      },

      clearCompletedForList: async (listId: string) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId = getEffectiveFamilyOwnerId();
        const familyId = ownerId || useAuthStore.getState().user?.familyId;
        const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
        const res = await apiPost<{ success: boolean }>(`/custom-checklists/clear-completed${qs}`, { listId });
        if (res.success) {
          set((state) => ({ items: state.items.filter(i => !(i.listId === listId && i.completed)) }));
        }
      },

      uncheckAllForList: async (listId: string) => {
        const now = new Date().toISOString();
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId = getEffectiveFamilyOwnerId();
        const familyId = ownerId || useAuthStore.getState().user?.familyId;
        const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
        
        // Optimistically uncheck all items locally
        const prevItems = get().items;
        set((state) => ({
          items: state.items.map(i => 
            i.listId === listId && i.completed 
              ? { ...i, completed: false, updatedAt: now } 
              : i
          )
        }));

        // Send to server
        const res = await apiPost<{ success: boolean }>(`/custom-checklists/uncheck-all${qs}`, { listId });
        if (!res.success) {
          // Rollback on failure
          set({ items: prevItems });
        }
      },

      fetchFromServer: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) return;
        set({ isSyncing: true });
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId7 = getEffectiveFamilyOwnerId();
        const familyId7 = ownerId7 || useAuthStore.getState().user?.familyId;
        const qs7 = familyId7 ? `?familyId=${encodeURIComponent(familyId7)}` : '';
        const res = await apiGet<{ lists: CustomChecklist[]; items: CustomChecklistItem[] }>(`/custom-checklists/get${qs7}`);
        if (res.success && res.data) {
          set({ lists: res.data.lists || [], items: res.data.items || [], isSyncing: false });
        } else {
          set({ isSyncing: false });
        }
      },

      clearAndSync: async () => {
        // Clear local state immediately to prevent showing stale data
        set({ lists: [], items: [], isSyncing: true });
        // Fetch fresh data from server
        await get().fetchFromServer();
      },

      checkRecurring: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) return;
        
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId = getEffectiveFamilyOwnerId();
        const familyId = ownerId || useAuthStore.getState().user?.familyId;
        const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
        
        const res = await apiPost<{ resetCount: number; resetListIds: string[]; lists: CustomChecklist[]; items: CustomChecklistItem[] }>(`/custom-checklists/check-recurring${qs}`, {});
        
        if (res.success && res.data) {
          // Update local state with reset lists and items
          set({ lists: res.data.lists, items: res.data.items });
          
          // Log activity for each reset list
          if (res.data.resetCount > 0 && res.data.resetListIds) {
            for (const listId of res.data.resetListIds) {
              logCustomChecklistAction('RESET_RECURRING_CHECKLIST', listId);
            }
          }
        }
      },

      getItemsForList: (listId: string) => {
        return get().items
          .filter(i => i.listId === listId)
          .slice()
          .sort((a,b) => {
            const oa = typeof a.order === 'number' ? a.order : 0;
            const ob = typeof b.order === 'number' ? b.order : 0;
            if (oa !== ob) return oa - ob;
            return a.createdAt.localeCompare(b.createdAt);
          });
      },

      moveItemUp: (id: string) => {
        const items = get().items.slice();
        const idx = items.findIndex(i => i.id === id);
        if (idx === -1) return;
        const item = items[idx];
        // find previous item in same list when ordered by order
        let same = items.filter(i => i.listId === item.listId).slice().sort((a,b) => (typeof a.order === 'number' ? a.order : 0) - (typeof b.order === 'number' ? b.order : 0));
        const pos = same.findIndex(i => i.id === id);
        if (pos <= 0) return; // already at top

        // If orders are missing or duplicate, bootstrap sequential orders (1..n)
        const needsBootstrap = same.some((s, i) => {
          if (typeof s.order !== 'number') return true;
          if (i === 0) return false;
          const prev = same[i - 1];
          const prevOrder = typeof prev?.order === 'number' ? prev.order : 0;
          return prevOrder >= (typeof s.order === 'number' ? s.order : 0);
        });
        if (needsBootstrap) {
          const boot = same.map((s, i) => ({ ...s, order: i + 1 }));
          set((state) => ({ items: state.items.map(it => {
            const b = boot.find(x => x.id === it.id);
            return b ? { ...it, order: b.order } : it;
          }) }));
          // optimistic server sync for bootstrapped orders
          (async () => {
            const { isAuthenticated, user } = useAuthStore.getState();
            if (!isAuthenticated) return;
            const { getEffectiveFamilyOwnerId } = await import('./family-store');
            const ownerId = getEffectiveFamilyOwnerId();
            const familyId = ownerId || user?.familyId;
            const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
            try {
              for (const b of boot) {
                await apiPut(`/custom-checklists/items/update/${b.id}${qs}`, { order: b.order });
              }
            } catch {
              get().fetchFromServer();
            }
          })();
          // refresh local 'same' after bootstrapping
          const refreshed = get().items.slice().filter(i => i.listId === item.listId).sort((a,b) => (typeof a.order === 'number' ? a.order : 0) - (typeof b.order === 'number' ? b.order : 0));
          same = refreshed;
        }

        const prev = same[same.findIndex(i => i.id === id) - 1];
        // swap orders
        const aOrder = typeof item.order === 'number' ? item.order : 0;
        const bOrder = typeof prev.order === 'number' ? prev.order : 0;
        set((state) => ({ items: state.items.map(i => {
          if (i.id === item.id) return { ...i, order: bOrder };
          if (i.id === prev.id) return { ...i, order: aOrder };
          return i;
        }) }));

        // Optimistic server sync for both items
        (async () => {
          const { isAuthenticated, user } = useAuthStore.getState();
          if (!isAuthenticated) return;
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId = getEffectiveFamilyOwnerId();
          const familyId = ownerId || user?.familyId;
          const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
          try {
            await apiPut(`/custom-checklists/items/update/${item.id}${qs}`, { order: bOrder });
            await apiPut(`/custom-checklists/items/update/${prev.id}${qs}`, { order: aOrder });
            } catch {
              // On failure, refetch from server to restore
              get().fetchFromServer();
            }
        })();
      },

      moveItemDown: (id: string) => {
        const items = get().items.slice();
        const idx = items.findIndex(i => i.id === id);
        if (idx === -1) return;
        const item = items[idx];
        let same = items.filter(i => i.listId === item.listId).slice().sort((a,b) => (typeof a.order === 'number' ? a.order : 0) - (typeof b.order === 'number' ? b.order : 0));
        const pos = same.findIndex(i => i.id === id);
        if (pos === -1 || pos >= same.length - 1) return; // already at bottom

        // Bootstrap if needed (missing/duplicate orders)
        const needsBootstrap = same.some((s, i) => {
          if (typeof s.order !== 'number') return true;
          if (i === 0) return false;
          const prev = same[i - 1];
          const prevOrder = typeof prev?.order === 'number' ? prev.order : 0;
          return prevOrder >= (typeof s.order === 'number' ? s.order : 0);
        });
        if (needsBootstrap) {
          const boot = same.map((s, i) => ({ ...s, order: i + 1 }));
          set((state) => ({ items: state.items.map(it => {
            const b = boot.find(x => x.id === it.id);
            return b ? { ...it, order: b.order } : it;
          }) }));
          (async () => {
            const { isAuthenticated, user } = useAuthStore.getState();
            if (!isAuthenticated) return;
            const { getEffectiveFamilyOwnerId } = await import('./family-store');
            const ownerId = getEffectiveFamilyOwnerId();
            const familyId = ownerId || user?.familyId;
            const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
            try {
              for (const b of boot) {
                await apiPut(`/custom-checklists/items/update/${b.id}${qs}`, { order: b.order });
              }
            } catch {
              get().fetchFromServer();
            }
          })();
          const refreshed = get().items.slice().filter(i => i.listId === item.listId).sort((a,b) => (typeof a.order === 'number' ? a.order : 0) - (typeof b.order === 'number' ? b.order : 0));
          same = refreshed;
        }

        const next = same[same.findIndex(i => i.id === id) + 1];
        const aOrder = typeof item.order === 'number' ? item.order : 0;
        const bOrder = typeof next.order === 'number' ? next.order : 0;
        set((state) => ({ items: state.items.map(i => {
          if (i.id === item.id) return { ...i, order: bOrder };
          if (i.id === next.id) return { ...i, order: aOrder };
          return i;
        }) }));

        (async () => {
          const { isAuthenticated, user } = useAuthStore.getState();
          if (!isAuthenticated) return;
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId = getEffectiveFamilyOwnerId();
          const familyId = ownerId || user?.familyId;
          const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
          try {
            await apiPut(`/custom-checklists/items/update/${item.id}${qs}`, { order: bOrder });
            await apiPut(`/custom-checklists/items/update/${next.id}${qs}`, { order: aOrder });
          } catch {
            get().fetchFromServer();
          }
        })();
      },
    }),
    {
      name: 'custom-checklists-store',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export default useCustomChecklistStore;
